/**
 * StaffSubscriptions — IT-SCE Intern: Service-Instanzen aller Kunden.
 *
 * TQ #9695 (Staff-Migration Teil B), portiert von
 * customer-portal/src/pages/staff/StaffSubscriptions.tsx. Backend admin-api
 * GET /admin/instances (services/api.ts staffListInstances).
 */

import { useEffect, useState } from 'react'
import { api } from '../services/api'
import type { StaffInstance } from '../services/api'
import { useToast } from '../components/ui'

const STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
  active: { bg: '#d1fae5', fg: '#065f46' },
  provisioning: { bg: '#dbeafe', fg: '#1e40af' },
  suspended: { bg: '#f3f4f6', fg: '#374151' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b' },
  failed: { bg: '#fee2e2', fg: '#991b1b' },
}

export default function StaffSubscriptions() {
  const [instances, setInstances] = useState<StaffInstance[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const { showToast } = useToast()

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const resp = await api.staffListInstances()
        if (!cancelled) setInstances(resp.instances ?? [])
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : String((err as { error?: string }).error ?? err)
        showToast('error', `Laden fehlgeschlagen: ${msg}`)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = instances.filter(i => {
    if (statusFilter !== 'all' && i.status !== statusFilter) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (i.instance_name + ' ' + (i.display_name ?? '') + ' ' + (i.service_name ?? '') + ' ' + (i.customer_email ?? '') + ' ' + (i.domain ?? '')).toLowerCase().includes(q)
  })

  const statusCounts = instances.reduce<Record<string, number>>((acc, i) => {
    acc[i.status] = (acc[i.status] ?? 0) + 1
    return acc
  }, {})

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>🔁 Alle Subscriptions</h1>
        <p className="page-subtitle">Service-Instanzen aller Kunden ({instances.length})</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suche Instance, Service, Kunde, Domain…"
          style={{ flex: 1, minWidth: 240, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        {(['all', 'active', 'provisioning', 'suspended', 'cancelled', 'failed'] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '6px 12px',
              background: statusFilter === s ? '#2563eb' : '#fff',
              color: statusFilter === s ? '#fff' : '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {s === 'all' ? `Alle (${instances.length})` : `${s} (${statusCounts[s] ?? 0})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade Subscriptions…</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Service / Instance</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Kunde</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Domain</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Nächste Abrechnung</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => {
                const badge = STATUS_BADGE[i.status] ?? { bg: '#f3f4f6', fg: '#374151' }
                return (
                  <tr key={i.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 500 }}>{i.service_name ?? i.service_slug ?? '—'}</div>
                      <div style={{ color: '#6b7280', fontSize: 11 }}>{i.display_name || i.instance_name}</div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {i.customer_name && <div>{i.customer_name}</div>}
                      <div style={{ color: '#9ca3af', fontSize: 11 }}>{i.customer_email}</div>
                    </td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                      {i.domain ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '2px 8px', background: badge.bg, color: badge.fg, borderRadius: 12, fontSize: 12 }}>
                        {i.status}
                      </span>
                      {i.payment_status && i.payment_status !== 'paid' && (
                        <span style={{ marginLeft: 4, padding: '2px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 12, fontSize: 11 }}>
                          {i.payment_status}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                      {i.next_billing_date ? new Date(i.next_billing_date).toLocaleDateString('de-DE') : '—'}
                      {i.billing_cycle && <div style={{ fontSize: 11, color: '#9ca3af' }}>{i.billing_cycle}</div>}
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine Subscriptions gefunden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
